<template>
  <div class="tab-text ml-0">
    <vx-card no-shadow class="custom-min-height">
      <!-- 1) Input Section -->
      <InputSection :form="form" @calculate="calculate" :onAddPayment="addPayment" :onRemovePayment="removePayment" :onClearPayments="clearPayments"/>

      <!-- 2) Summary Box -->
      <SummaryBox :form="form"/>

      <!-- 3) Payment Schedule Table -->
      <PaymentScheduleTable :payment-rounds="form.paymentRounds"/>
    </vx-card>
  </div>
</template>

<script>
import InputSection from './components/InputSection/InputSection.vue'
import SummaryBox from './components/SummaryBox.vue'
import PaymentScheduleTable from './components/PaymentScheduleTable.vue'

import moment from 'moment/moment'
import pmt from 'formula-pmt'
import {mRound, numberFormat} from '@/utils'
import {Thai} from 'flatpickr/dist/l10n/th'
import 'flatpickr/dist/themes/dark.css'

export default {
  name: 'InstallmentCalculationRestructuringCard',
  components: {InputSection, SummaryBox, PaymentScheduleTable},

  data() {
    return {
      form: {
        // flatpickr config
        configPaymentDue: {
          monthSelectorType: 'static',
          allowInput: true,
          locale: Thai,
          dateFormat: 'd-m-Y',
          disable: [
            (date) => moment(date).date() !== moment(date).endOf('month').date()
          ]
        },

        // inputs
        payments: [
          {date: "", fee: 0, penalty: 0, interest: 0, principal: 0},
        ],

        prosecutionFee: {
          lawyerFee: 0,
          reminderLetterFee: 0,
          courtFee: 0,
          summonFee: 0,
          documentFee: 0,
          enforceFee: 0,
          litigationFee: 0
        },
        taxAmount: 0,
        restructuringFee: 0,
        restructuringPenalty: 0,

        restructuringInterest: {
          interestAsPerLawsuit: 0,
          interestUntilLawsuitDate: 0,
          interestAfterLawsuitDate: 0
        },

        principle: 0,
        installmentCount: 0,
        interestRate: 12,
        paymentDue: moment().endOf('month').format('DD-MM-YYYY'),

        // summary outputs
        netRestructuringAmount: 0,
        netPrincipalAmount: 0,
        paymentRoundAmountPrincipal: 0,
        paymentRoundAmountRestructuring: 0,
        paymentRoundAmount: 0,
        paymentRoundAmountLast: 0,
        totalRemain: 0,
        firstPaymentDate: '',
        lastPaymentDate: '',
        paymentRounds: []
      }
    }
  },

  methods: {
    /**
     * ตรวจสอบความถูกต้องก่อนคำนวณ
     */



    validateBeforeCalculate({restructuringAmount, principalAmount}) {

      if ((principalAmount + restructuringAmount) <= 0) {
        this.$vs.notify({
          title: 'ข้อผิดพลาด',
          text: 'เงินต้นคงเหลือและยอดยกมาคงเหลือรวมกันต้องมากกว่า 0',
          color: 'danger',
          time: 5000 // แสดง 8 วินาที
        })
        return false
      }

      if (this.form.installmentCount <= 0 || !this.form.paymentDue) {
        this.$vs.notify({
          title: 'ข้อผิดพลาด',
          text: 'กรุณากรอกจำนวนงวดและรอบชำระเดือนแรก',
          color: 'danger',
          time: 5000 // แสดง 8 วินาที
        })
        return false
      }

      return true
    },

    /**
     * คำนวณค่างวดรายเดือน (PMT)
     */
    calculateInstallment(principalAmount, restructuringAmount) {
      const monthlyRate = (Number(this.form.interestRate) / 100) / 12
      const periods = Number(this.form.installmentCount)

      const principalPMT = mRound(-pmt(monthlyRate, periods, principalAmount), 10)
      const restructuringPMT = mRound(restructuringAmount / periods, 10)

      return {principalPMT, restructuringPMT, periods}
    },

    /**
     * สร้างตารางการชำระหนี้
     */
    generatePaymentRounds(principalPMT, restructuringPMT, principalAmount, restructuringAmount, periods) {
      let principalRemain = principalAmount
      let restructuringRemain = restructuringAmount

      let currentDate = moment(
        this.$options.filters.convDDMMYYYYToYYYYMMDD(this.form.paymentDue.trim())
      )

      const rounds = []

      for (let i = 1; i <= periods; i++) {
        const monthStart = moment(currentDate).startOf('month')
        const nextDate = moment(currentDate).add(1, 'day').endOf('month')
        const daysInPeriod = currentDate.diff(monthStart, 'days') + 1

        // แถวแรก (ก่อนงวดที่ 1)
        if (i === 1) {
          rounds.push({
            no: '-',
            date: monthStart.locale('th').format('DD MMM YY'),
            days: '-',
            paymentRoundAmount: '-',
            paymentInterest: '-',
            paymentPrincipal: '-',
            principalRemain: numberFormat(principalRemain),
            paymentRestructuring: '-',
            restructuringRemain: numberFormat(restructuringRemain)
          })
        }

        const interest = principalRemain * ((this.form.interestRate / 100) / 12)
        const payPrincipal = principalPMT - interest
        principalRemain -= payPrincipal

        const payRestructuring = restructuringPMT
        restructuringRemain -= payRestructuring

        rounds.push({
          no: i,
          date: currentDate.locale('th').format('DD MMM YY'),
          days: daysInPeriod,
          paymentRoundAmount: numberFormat(principalPMT),
          paymentInterest: numberFormat(interest),
          paymentPrincipal: numberFormat(payPrincipal),
          principalRemain: numberFormat(principalRemain),
          paymentRestructuring: numberFormat(payRestructuring),
          restructuringRemain: numberFormat(restructuringRemain)
        })

        currentDate = nextDate
      }

      return {rounds, principalRemain, restructuringRemain}
    },

    /**
     * main calculate function
     */
    async calculate({restructuringAmount, principalAmount}) {
      if (!this.validateBeforeCalculate({restructuringAmount, principalAmount})) return

      // ✅ ตรวจสอบและหักค่าที่ติดลบ
      if (restructuringAmount < 0) {
        principalAmount += restructuringAmount   // หักเข้า principal
        restructuringAmount = 0
      }
      if (principalAmount < 0) {
        restructuringAmount += principalAmount   // หักเข้า restructuring
        principalAmount = 0
      }

      this.$vs.loading()
      this.form.paymentRounds = []
      this.form.netRestructuringAmount = numberFormat(restructuringAmount)
      this.form.netPrincipalAmount = numberFormat(principalAmount)

      const {principalPMT, restructuringPMT, periods} =
        this.calculateInstallment(principalAmount, restructuringAmount)

      const {rounds, principalRemain, restructuringRemain} =
        this.generatePaymentRounds(principalPMT, restructuringPMT, principalAmount, restructuringAmount, periods)

      this.form.paymentRounds = rounds

      // summary
      const paymentRoundAmount = principalPMT + restructuringPMT
      const totalRemain = principalRemain + restructuringRemain

      this.form.paymentRoundAmountPrincipal = numberFormat(principalPMT)
      this.form.paymentRoundAmountRestructuring = numberFormat(restructuringPMT)
      this.form.paymentRoundAmount = numberFormat(paymentRoundAmount)
      this.form.totalRemain = numberFormat(totalRemain)
      this.form.paymentRoundAmountLast = numberFormat(totalRemain + paymentRoundAmount)

      // firstPaymentDate (หลีกเลี่ยง ?. operator)
      this.form.firstPaymentDate =
        (rounds.length > 1 && rounds[1] && rounds[1].date) ? rounds[1].date : ''

      // lastPaymentDate (หลีกเลี่ยง ?. operator)
      const lastIdx = rounds.length - 1
      this.form.lastPaymentDate =
        (lastIdx >= 0 && rounds[lastIdx] && rounds[lastIdx].date) ? rounds[lastIdx].date : ''

      setTimeout(() => this.$vs.loading.close(), 300)
    },

    addPayment(data) {
      if (data) {
        this.form.payments.push({
          date: this.$options.filters.convYYYYMMDDToDDMMYYYY(data.transaction.transactionDate),
          fee: data.transaction.paidAccruedFee,
          penalty: data.transaction.paidAccruedPenalty,
          interest: data.transaction.paidAccruedInterest,
          principal: data.transaction.paidPrinciple
        })
      } else {
        this.form.payments.push({date: "", fee: 0, penalty: 0, interest: 0, principal: 0})
      }
    },

    clearPayments() {
      this.form.payments = []
    },

    removePayment(index) {
      if (this.form.payments.length <= 1) {
        return
      }
      if (index < 0 || index >= this.form.payments.length) return
      this.form.payments.splice(index, 1)
    },


  }
}
</script>
